// ─────────────────────────────────────────────────────────
// utils/format.ts  —  Formatting utilities
//
// Ported exactly from monolith. Every function signature and
// output matches the original — these are used in PDF/CSV
// exports and salary slips where exact formatting matters.
// ─────────────────────────────────────────────────────────

// ── ₹ formatting — Indian locale ─────────────────────────
// Replaces monolith fmt(n)
export function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '₹0'
  return '₹' + Math.round(n).toLocaleString('en-IN')
}

// ── Date formatting — "23 Mar 2026" ──────────────────────
// Replaces monolith fmtD(d)
export function fmtD(d: string | null | undefined): string {
  if (!d) return '—'
  try {
    const dt = new Date(d)
    return dt.toLocaleDateString('en-IN', {
      day:   '2-digit',
      month: 'short',
      year:  'numeric',
    })
  } catch {
    return d
  }
}

// ── Full date string for display ──────────────────────────
// Replaces monolith fmtDate(d)
export function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return '—'
  try {
    const dt = typeof d === 'string' ? new Date(d) : d
    return dt.toLocaleDateString('en-IN', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    })
  } catch {
    return String(d)
  }
}

// ── Number → Indian words (for salary slips) ─────────────
// Replaces monolith toW(n) + hw(n)
// Used in salary slips: "Rupees Twenty-Five Thousand Only"
export function toW(n: number): string {
  if (!n || n <= 0) return 'Zero'

  const ones = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven',
    'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen',
    'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
  ]
  const tens = [
    '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty',
    'Sixty', 'Seventy', 'Eighty', 'Ninety',
  ]

  function hw(num: number): string {
    if (num === 0)       return ''
    if (num < 20)        return ones[num]! + ' '
    if (num < 100)       return tens[Math.floor(num / 10)]! + ' ' + (num % 10 ? ones[num % 10]! + ' ' : '')
    if (num < 1000)      return ones[Math.floor(num / 100)]! + ' Hundred ' + (num % 100 ? hw(num % 100) : '')
    if (num < 100_000)   return hw(Math.floor(num / 1000)) + 'Thousand ' + (num % 1000 ? hw(num % 1000) : '')
    if (num < 10_000_000)return hw(Math.floor(num / 100_000)) + 'Lakh ' + (num % 100_000 ? hw(num % 100_000) : '')
    return hw(Math.floor(num / 10_000_000)) + 'Crore ' + (num % 10_000_000 ? hw(num % 10_000_000) : '')
  }

  return hw(Math.round(n)).trim()
}

// ── Initials from name ────────────────────────────────────
// Replaces monolith ini(n)
export function ini(n: string | null | undefined): string {
  if (!n) return 'U'
  const parts = n.trim().split(' ')
  if (parts.length > 1) {
    return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase()
  }
  return n.slice(0, 2).toUpperCase()
}

// ── Mask sensitive numbers ────────────────────────────────
// Replaces monolith mask(s, show)
// Used for account numbers, PAN, etc.
export function mask(s: string | null | undefined, show = 4): string {
  if (!s) return '—'
  return '••••' + s.slice(-show)
}

// ── Zero-pad a number ─────────────────────────────────────
// Replaces monolith pad2(n)
export function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

// ── Today's date as ISO string ────────────────────────────
// Replaces monolith today()
export function today(): string {
  return new Date().toISOString().split('T')[0]!
}

// ── Attendance month key ──────────────────────────────────
// Replaces monolith ak(m, y) → "2026-3"
export function ak(m: number, y: number): string {
  return `${y}-${m + 1}`
}

// ── Days in a month ───────────────────────────────────────
// Replaces monolith dim(m, y)
export function dim(m: number, y: number): number {
  return new Date(y, m + 1, 0).getDate()
}

// ── Salary key helpers ────────────────────────────────────
// Replaces monolith skEmpId / skKey
// SAL-9: empId may contain hyphens — slice from index 2 onward
export function skEmpId(sk: string): string {
  const parts = sk.split('-')
  return parts.length >= 3 ? parts.slice(2).join('-') : ''
}

export function skKey(sk: string): string {
  const parts = sk.split('-')
  return parts.length >= 2 ? `${parts[0]}-${parts[1]}` : ''
}

// ── Short unique ID ───────────────────────────────────────
// Replaces monolith uid()
export function uid(): string {
  return Math.random().toString(36).slice(2, 11)
}

// ── Colour class for attendance status ───────────────────
// Replaces monolith ccls(v)
export function ccls(v: string): string {
  const map: Record<string, string> = {
    P:        'cP',
    HD:       'cHD',
    A:        'cA',
    H:        'cH',
    WO:       'cWO',
    'P+0.5OT':'cO1',
    'P+1OT':  'cO2',
    'P+1.5OT':'cO3',
    'P+2OT':  'cO4',
  }
  return map[v] ?? ''
}

// ── Month names (used in dropdowns) ──────────────────────
export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

// ── Expense categories ────────────────────────────────────
export const EXP_CATS = [
  'Materials', 'Labour', 'Equipment', 'Transport', 'Fuel',
  'Site Expenses', 'Office Expenses', 'Utilities', 'Repairs',
  'Professional Fees', 'Subcontractor', 'Miscellaneous',
]
